import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, Navigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Dropdown, Modal, Button } from 'react-bootstrap';
import { FaFileExcel, FaFilePdf } from 'react-icons/fa';
import { FiArrowLeft, FiStar } from 'react-icons/fi';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import BackToTop from '../components/BackToTop/BackToTop';
import { formatDateTimeToDDMMMYYYY } from '../../utils/dateFormatter';
import {
  getForum,
  getForumComments,
  changeForumStatus,
  toggleForumFavorite,
  deleteForum,
  exportForumPdf,
} from '../../services/ForumService';
import ForumCommentSection from './ForumCommentSection';
import { getForumPermissions } from './forumPermissions';
import { useCanDo } from '../../hooks/useCanDo';
import { getForumParticipantDisplayList } from './forumParticipants';
import { useForumAccess } from '../../hooks/useForumAccess';
import '../tasks/taskdetails.css';
import '../Documents/commonstyle.css';
import { getCommentIdFromLocation } from '../../utils/commentDeepLink';
import { navigateBackSafely } from '../../utils/navigateBackSafely';
import { getReturnToFallback, pickQuerySuffix, withListQuery } from '../../utils/listQueryPersistence';
import { usePageToolbar } from '../layouts/nav/PageToolbarContext';

const FORUM_LIST_QUERY_KEYS = [
  'search',
  'status',
  'show_closed',
  'created_start',
  'created_end',
  'participant_id',
  'page',
];

const STATUS_ACTIONS = [
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'closed', label: 'Closed' },
];

const statusLabel = (s) => (s || '').replace('_', ' ');

function findCommentInTree(commentList, commentId) {
  for (const comment of commentList || []) {
    if (String(comment.id) === String(commentId)) return comment;
    const inReply = findCommentInTree(comment.replies, commentId);
    if (inReply) return inReply;
  }
  return null;
}

function getCommentPreview(comment) {
  if (!comment) return 'Comment';
  if (comment.subject?.trim()) return comment.subject.trim();
  const plain = (comment.content || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  return plain.slice(0, 60) || 'Comment';
}

const TASK_STATUS_LABELS = {
  '-1': 'Overdue',
  0: 'Pending',
  1: 'In Progress',
  2: 'Blocked',
  3: 'Completed',
  4: 'Paused',
  5: 'Partial',
};

function getTaskStatusLabel(status) {
  const key = typeof status === 'number' ? status : parseInt(status, 10);
  return TASK_STATUS_LABELS[key] ?? 'Unknown';
}

function getTaskStatusClass(status) {
  const key = typeof status === 'number' ? status : parseInt(status, 10);
  switch (key) {
    case 3: return 'forum-info-task-status--completed';
    case 1: return 'forum-info-task-status--progress';
    case 2: return 'forum-info-task-status--blocked';
    case 4: return 'forum-info-task-status--paused';
    case 5: return 'forum-info-task-status--partial';
    case -1: return 'forum-info-task-status--overdue';
    default: return 'forum-info-task-status--pending';
  }
}

function getTaskPriorityClass(priority) {
  switch ((priority || '').toLowerCase()) {
    case 'high': return 'forum-info-task-priority--high';
    case 'low': return 'forum-info-task-priority--low';
    default: return 'forum-info-task-priority--medium';
  }
}

const ForumDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const forumListFallback = getReturnToFallback(
    location.search,
    withListQuery(
      '/forum/list',
      pickQuerySuffix(location.search, FORUM_LIST_QUERY_KEYS)
    )
  );
  const { hasAccess, loading: accessLoading } = useForumAccess();
  const canEditForum = useCanDo('forum', 'create_edit');
  const canDeleteForum = useCanDo('forum', 'delete');
  const canExportForum = useCanDo('forum', 'export');
  const scrollToCommentId = getCommentIdFromLocation(location);
  const [forum, setForum] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [comments, setComments] = useState([]);
  const [relatedTasks, setRelatedTasks] = useState([]);
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(true);
  const [infoOpen, setInfoOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const load = async ({ showLoading = true } = {}) => {
    const softDenyAndLeave = (message) => {
      try { sessionStorage.removeItem('redirectAfterLogin'); } catch (e) { /* ignore */ }
      toast.error(message || "Access denied. You don't have permission to view this discussion.");
      setForum(null);
      setTimeout(() => {
        if (showLoading) setLoading(false);
        navigate(forumListFallback);
      }, 2000);
    };

    try {
      if (showLoading) setLoading(true);
      const [forumRes, commentsRes] = await Promise.all([
        getForum(id),
        getForumComments(id),
      ]);
      if (forumRes.data?.success === false) {
        const msg = forumRes.data.message || 'Failed to load forum';
        if (/access denied/i.test(msg)) {
          softDenyAndLeave(msg);
          return;
        }
        toast.error(msg);
        setForum(null);
        return;
      }
      if (commentsRes.data?.success === false) {
        const msg = commentsRes.data.message || 'Failed to load comments';
        if (/access denied/i.test(msg)) {
          softDenyAndLeave(msg);
          return;
        }
        toast.error(msg);
      }
      setForum(forumRes.data.forum);
      setAttachments(forumRes.data.attachments || []);
      setRelatedTasks(forumRes.data.related_tasks || []);
      setIsFavorite(forumRes.data.is_favorite);
      setComments(commentsRes.data?.comments || []);
    } catch (err) {
      const status = err.response?.status;
      if (status === 403 || status === 409) {
        softDenyAndLeave(
          err.response?.data?.message ||
          "Access denied. You don't have permission to view this discussion."
        );
        return;
      }
      if (status === 404) {
        softDenyAndLeave(err.response?.data?.message || 'Discussion not found.');
        return;
      }
      toast.error(err.response?.data?.message || 'Failed to load forum');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    const root = document.documentElement;
    if (infoOpen) {
      root.classList.add('forum-info-panel-open');
    } else {
      root.classList.remove('forum-info-panel-open');
    }
    return () => root.classList.remove('forum-info-panel-open');
  }, [infoOpen]);

  const handleStatusChange = async (status) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const { canSetClosed, canChangeStatus } = getForumPermissions(forum, user);
    if (!canChangeStatus) {
      toast.error('You are not allowed to change the status');
      return;
    }
    if (status === 'closed' && !canSetClosed) {
      toast.error('You are not allowed to close this discussion');
      return;
    }
    try {
      const res = await changeForumStatus(id, status);
      if (res.data?.success === false) {
        toast.error(res.data.message || 'Failed to update status');
        return;
      }
      toast.success('Status updated');
      load({ showLoading: false });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update status');
    }
  };

  const handleFavorite = async () => {
    try {
      const res = await toggleForumFavorite(id);
      if (res.data?.success === false) {
        toast.error(res.data.message || 'Failed to update favorite');
        return;
      }
      setIsFavorite(res.data.is_favorite);
      toast.success(res.data.is_favorite ? 'Added to favorites' : 'Removed from favorites');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update favorite');
    }
  };

  const handleDeleteWithConfirmation = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setShowDeleteModal(false);
    try {
      const res = await deleteForum(id);
      if (res.data?.success === false) {
        toast.error(res.data.message || 'Delete failed');
        return;
      }
      toast.success('Discussion deleted');
      navigate(forumListFallback);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  const handleCreateTask = (comment) => {
    if (!comment?.id) {
      toast.error('Comment is missing an id. Refresh the page.');
      return;
    }
    window.open(`/task/addtask?forumCommentId=${comment.id}`, '_blank');
    toast.info('Task form opened in new tab with prefilled data');
  };

  const scrollToComment = (commentId) => {
    if (!commentId) return;
    setInfoOpen(false);
    setTimeout(() => {
      const el = document.getElementById(`comment-${commentId}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('comment-highlight');
      setTimeout(() => el.classList.remove('comment-highlight'), 2000);
    }, 300);
  };

  const handleExportPdf = async () => {
    try {
      const res = await exportForumPdf(id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `forum_${id}.pdf`;
      link.click();
    } catch (err) {
      toast.error('PDF export failed');
    }
  };

  const forumDetailToolbar = useMemo(() => {
    let endActions = null;
    if (forum) {
      const userdata = JSON.parse(localStorage.getItem('user') || '{}');
      const { canEdit, canSetClosed, canDelete, canChangeStatus } = getForumPermissions(forum, userdata);
      const visibleStatusActions = STATUS_ACTIONS.filter(
        (s) => s.value !== 'closed' || canSetClosed
      );
      const currentStatusAction = STATUS_ACTIONS.find((s) => s.value === forum.status);
      const canUseStatusMenu = canChangeStatus && visibleStatusActions.length > 0 && forum.status !== 'closed';

      endActions = (
        <div className="page-toolbar-bar__end">
          <button
            type="button"
            className={`header-toolbar-btn${isFavorite ? ' is-active' : ''}`}
            onClick={handleFavorite}
            aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            title={isFavorite ? 'Unfavorite' : 'Favorite'}
          >
            <FiStar aria-hidden />
          </button>
          <div className="forum-title-row__status">
            {canUseStatusMenu ? (
              <Dropdown align="end" className="forum-status-menu">
                <Dropdown.Toggle
                  variant="link"
                  className="forum-title-row-btn forum-status-btn"
                  aria-label="Change discussion status"
                >
                  <span className="forum-status-btn__label">
                    {currentStatusAction?.label || statusLabel(forum.status)}
                  </span>
                  <i className="fas fa-chevron-down forum-status-btn__chevron" aria-hidden></i>
                </Dropdown.Toggle>
                <Dropdown.Menu
                  className="forum-status-menu__dropdown"
                  popperConfig={{ strategy: 'fixed' }}
                  renderOnMount
                >
                  {visibleStatusActions
                    .filter((s) => s.value !== forum.status)
                    .map((s) => (
                      <Dropdown.Item
                        key={s.value}
                        onClick={() => handleStatusChange(s.value)}
                      >
                        {s.label}
                      </Dropdown.Item>
                    ))}
                </Dropdown.Menu>
              </Dropdown>
            ) : (
              <span className="forum-title-row-btn forum-status-btn forum-status-btn--readonly">
                {currentStatusAction?.label || statusLabel(forum.status)}
              </span>
            )}
          </div>
          <button
            type="button"
            className={`header-toolbar-btn${infoOpen ? ' is-active' : ''}`}
            onClick={() => setInfoOpen((open) => !open)}
            aria-expanded={infoOpen}
            aria-controls="forum-info-panel"
            title="Info"
            aria-label="Info"
          >
            <i className="fas fa-info-circle" aria-hidden></i>
          </button>
          {canExportForum ? (
            <>
          <button
            type="button"
            className="header-toolbar-btn"
            onClick={() => navigate(`/forum/detail/${id}/daily-updates`)}
            title="Transaction Statement"
            aria-label="Transaction Statement"
          >
            <FaFileExcel aria-hidden />
          </button>
          <button
            type="button"
            className="header-toolbar-btn"
            onClick={handleExportPdf}
            title="Export PDF"
            aria-label="Export PDF"
          >
            <FaFilePdf aria-hidden />
          </button>
            </>
          ) : null}
          {canEdit && (
            <button
              type="button"
              className="header-toolbar-btn"
              onClick={() => navigate(`/forum/edit/${id}${pickQuerySuffix(location.search, FORUM_LIST_QUERY_KEYS)}`)}
              title="Edit"
              aria-label="Edit"
            >
              <i className="fa fa-edit" aria-hidden></i>
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              className="header-toolbar-btn"
              onClick={handleDeleteWithConfirmation}
              title="Delete"
              aria-label="Delete"
            >
              <i className="fa fa-trash" aria-hidden></i>
            </button>
          )}
        </div>
      );
    }

    return (
      <>
        <div className="page-toolbar-bar__start">
          <button
            type="button"
            className="page-toolbar-back-btn"
            onClick={() => navigateBackSafely(navigate, forumListFallback)}
            title="Back"
            aria-label="Back"
          >
            <FiArrowLeft />
            Back
          </button>
        </div>
        {endActions}
      </>
    );
  }, [forum, isFavorite, infoOpen, id, location.search, navigate, forumListFallback, canEditForum, canDeleteForum, canExportForum]);

  usePageToolbar(forumDetailToolbar, [forumDetailToolbar]);

  if (accessLoading || loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="mt-3">
            <h5>Loading discussion...</h5>
            <p className="text-muted">Please wait while we fetch the discussion information.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return null;
  }

  if (!forum) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <h5>Discussion not found</h5>
          <p className="text-muted">
            The discussion you&apos;re looking for doesn&apos;t exist or you don&apos;t have permission to view it.
          </p>
          <button
            type="button"
            className="btn btn-primary mt-3"
            onClick={() => navigate(forumListFallback)}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const participantEntries = getForumParticipantDisplayList(forum);
  const forumCreatedAt = forum.created_at ? formatDateTimeToDDMMMYYYY(forum.created_at, true) : '';
  const forumUpdatedAt = forum.updated_at ? formatDateTimeToDDMMMYYYY(forum.updated_at, true) : '';
  const isForumEdited = forumUpdatedAt && forumCreatedAt && forumUpdatedAt !== forumCreatedAt;

  return (
    <>
      <div className="row forum-detail-row">
        <div className="col-xl-12 col-xxl-12">
          <div className="card heightcard forum-detail-card">
            <div className="card-header details-card-header forum-section-header forum-title-block">
              <div className="forum-title-block__info">
                <h5 className="text-base sm:text-lg font-semibold mb-0 forum-title-row__title">
                  <span className="forum-title-row__title-text">{forum.title}</span>
                </h5>
                <div className="forum-title-block__labels">
                  <span className="forum-title-meta-label">
                    Start Date: {forum.start_date ? formatDateTimeToDDMMMYYYY(forum.start_date, false) : 'N/A'}
                  </span>
                  <span className="forum-title-meta-label">
                    Priority: {forum.priority || 'Medium'}
                  </span>
                </div>
              </div>
            </div>
            <div className="card-body forum-detail-card__body">
              <div className="row">
                {(forum.description || isForumEdited) && (
                  <div className="mb-4">
                    {forum.description && (
                      <div
                        className="forum-description-content"
                        dangerouslySetInnerHTML={{ __html: forum.description }}
                        style={{ minHeight: '60px' }}
                      />
                    )}
                    {isForumEdited && (
                      <span className="comment-edited forum-description-edited">
                        Edited on {forumUpdatedAt}
                      </span>
                    )}
                  </div>
                )}

                {attachments.length > 0 && (
                  <div className="col-lg-12 mb-2">
                    <label className="text-label">Attachments</label>
                    <div className="attachments-container p-3" style={{ background: '#f9f9f9', borderRadius: 6 }}>
                      <div className="d-flex flex-wrap gap-2">
                        {attachments.map((a) => (
                          <span key={a.id} className="badge bg-white text-dark border px-3 py-2">{a.file_name}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>

          <div className="card heightcard forum-comments-card">
            <div className="card-header details-card-header forum-section-header">
              <h5 className="text-base sm:text-lg font-semibold mb-0">Comments</h5>
            </div>
            <div className="card-body forum-comments-card-body">
              <ForumCommentSection
                forumId={id}
                forum={forum}
                comments={comments}
                onCommentAdded={() => load({ showLoading: false })}
                onCreateTask={handleCreateTask}
                disabled={forum.status === 'closed'}
                scrollToCommentId={scrollToCommentId}
              />
            </div>
          </div>
        </div>
      </div>

      {infoOpen && (
        <button
          type="button"
          className="forum-info-backdrop"
          onClick={() => setInfoOpen(false)}
          aria-label="Close info panel"
        />
      )}

      <aside
        id="forum-info-panel"
        className={`forum-info-drawer${infoOpen ? ' forum-info-drawer--open' : ''}`}
        aria-hidden={!infoOpen}
      >
        <div className="forum-info-drawer-header">
          <h6 className="forum-info-drawer-title mb-0">Info</h6>
          <button
            type="button"
            className="forum-info-drawer-close"
            onClick={() => setInfoOpen(false)}
            aria-label="Close info panel"
          >
            <i className="fas fa-times" aria-hidden></i>
          </button>
        </div>
        <div className="forum-info-drawer-body">
          <div className="forum-info-field">
            <p className="forum-info-field-label">Created on</p>
            <p className="forum-info-field-value">
              {forum.created_at ? formatDateTimeToDDMMMYYYY(forum.created_at, true) : '—'}
            </p>
          </div>
          <div className="forum-info-field">
            <p className="forum-info-field-label">Creator</p>
            <p className="forum-info-field-value">{forum.creator?.full_name || '—'}</p>
          </div>
          <div className="forum-info-field">
            <p className="forum-info-field-label">Participants</p>
            {participantEntries.length > 0 ? (
              <div className="user-chip-list forum-info-participants">
                {participantEntries.map((entry) => (
                  <div className="user-chip" key={entry.id}>
                    <span className="user-chip-avatar">
                      {entry.type === 'org' ? (
                        <i className="fa fa-building" aria-hidden></i>
                      ) : (
                        entry.name[0]
                      )}
                    </span>
                    {entry.name}
                  </div>
                ))}
              </div>
            ) : (
              <p className="forum-info-field-value">—</p>
            )}
          </div>
          <div className="forum-info-field">
            <p className="forum-info-field-label">
              Tasks from comments ({relatedTasks.length})
            </p>
            {relatedTasks.length > 0 ? (
              <div className="forum-info-task-list">
                {relatedTasks.map((l) => {
                  const sourceComment = findCommentInTree(comments, l.comment_id);
                  const task = l.task || {};
                  const openTask = () => window.open(`/task/taskdetails/${task.id}`, '_blank', 'noopener,noreferrer');
                  return (
                    <article key={l.link_id} className="forum-info-task-card">
                      <div className="forum-info-task-card__top">
                        <button
                          type="button"
                          className="forum-info-task-card__title"
                          onClick={openTask}
                        >
                          {task.title || `Task #${task.id}`}
                        </button>
                        <button
                          type="button"
                          className="forum-info-task-card__open"
                          onClick={openTask}
                          aria-label={`Open task: ${task.title || task.id}`}
                        >
                          <i className="fas fa-external-link-alt" aria-hidden></i>
                        </button>
                      </div>
                      <div className="forum-info-task-card__badges">
                        <span className={`forum-info-task-status ${getTaskStatusClass(task.status)}`}>
                          {getTaskStatusLabel(task.status)}
                        </span>
                        <span className={`forum-info-task-priority ${getTaskPriorityClass(task.priority)}`}>
                          {task.priority || 'Medium'}
                        </span>
                      </div>
                      <div className="forum-info-task-card__meta">
                        {task.creator?.full_name && (
                          <span className="forum-info-task-card__creator">
                            <i className="fas fa-user" aria-hidden></i>
                            {task.creator.full_name}
                          </span>
                        )}
                        {l.comment_id && (
                          <button
                            type="button"
                            className="forum-info-task-card__source"
                            onClick={() => scrollToComment(l.comment_id)}
                          >
                            <i className="fas fa-comment" aria-hidden></i>
                            {getCommentPreview(sourceComment)}
                          </button>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <p className="forum-info-field-value">No tasks created from comments yet</p>
            )}
          </div>
          <div className="forum-info-field">
            <p className="forum-info-field-label">Email alerts</p>
            <p className="forum-info-field-value">{forum.email_notification ? 'Yes' : 'No'}</p>
          </div>
          <div className="forum-info-field">
            <p className="forum-info-field-label">Participants can edit</p>
            <p className="forum-info-field-value">{forum.allow_participants_edit ? 'Yes' : 'No'}</p>
          </div>
          <div className="forum-info-field">
            <p className="forum-info-field-label">Participants can close</p>
            <p className="forum-info-field-value">{forum.allow_participants_close ? 'Yes' : 'No'}</p>
          </div>
        </div>
      </aside>

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Discussion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete this discussion? Once deleted, you will not be able to recover this discussion!
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="doc-form-cancel-btn"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button
            className="doc-form-submit-btn"
            onClick={handleDeleteConfirm}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <BackToTop threshold={0} />
      <InternalFooter />
    </>
  );
};

export default ForumDetail;

/** Legacy split-view URL → standard detail page */
export function ForumListDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={`/forum/detail/${id}`} replace />;
}
